import { Fragment } from 'react';
import { useTranslations } from 'use-intl';
import { User, Users } from 'lucide-react';
import {
  Avatar,
  Body,
  Button,
  Dialog,
  DialogFooter,
  DialogSection,
  Divider,
  Eyebrow,
  Heading,
  I,
  Icon,
  Pill,
  Row,
  Stack,
} from '@leanin/ui';
import type { MembershipRequestView } from '../../../lib/queries/groups';

// Read-only reviewer view of a submitted application's answers (Network Member
// Request Upgrade, Phase 7). Renders the immutable `intake` snapshot in the
// wording AS ASKED, the consent evidence, and — for a scrubbed row (withdrawn /
// retention-expired / DSR, D3) — an "answers deleted" empty state. NEVER an
// input surface: `IntakeFormFields` is for applicants; this is the reviewer.
//
// Opened for any row whose `intake` is present (a snapshot or a tombstone).
// Legacy / flag-off rows have `intake == null` and get no "View application"
// affordance at all, so they render exactly as today.
//
// When the caller passes the review handlers (only the pending tab does), a
// sticky action bar hosts Approve / Decline / Message / View-profile — all of
// which reuse the SAME handlers the applicant-list card uses. No mutation or
// routing logic lives here.

type Intake = NonNullable<MembershipRequestView['intake']>;
type Snapshot = Extract<Intake, { answers: unknown }>;
type Tombstone = Extract<Intake, { scrubbed: true }>;

function isTombstone(intake: Intake): intake is Tombstone {
  return 'scrubbed' in intake && intake.scrubbed === true;
}

// Relative "Requested X ago" — mirrors the applicant-card formatter. Kept local
// so the reviewer modal doesn't take a dependency on the card component; both
// read the same `admin.requests.relative*` copy.
function formatRelative(
  iso: string,
  t: ReturnType<typeof useTranslations<'Group'>>,
): string {
  const then = Date.parse(iso);
  if (!Number.isFinite(then)) return '';
  const diffMs = Date.now() - then;
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return t('admin.requests.relativeJustNow');
  if (mins < 60) return t('admin.requests.relativeMinutes', { count: mins });
  const hours = Math.round(mins / 60);
  if (hours < 24) return t('admin.requests.relativeHours', { count: hours });
  const days = Math.round(hours / 24);
  if (days < 7) return t('admin.requests.relativeDays', { count: days });
  const weeks = Math.round(days / 7);
  if (weeks < 5) return t('admin.requests.relativeWeeks', { count: weeks });
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
}

type Props = {
  readonly request: MembershipRequestView | null;
  readonly onClose: () => void;
  readonly isPending?: boolean;
  readonly onApprove?: (requestId: string) => void;
  readonly onDecline?: (requestId: string) => void;
  readonly onMessage?: (userId: string) => void;
  readonly onViewProfile?: (userId: string) => void;
  readonly onViewCircle?: (circleId: string) => void;
  /** When set, this is the requester viewing THEIR OWN pending request: the
   *  footer gains a Withdraw affordance and the modal opens even when no form
   *  was submitted (a short summary stands in for the answers). */
  readonly onWithdraw?: () => void;
  /** When set (requester's own view), the footer gains an Edit affordance that
   *  re-opens the join form pre-filled so they can revise their answers. Only
   *  passed for rows whose answers are still editable (pending, live snapshot). */
  readonly onEdit?: () => void;
};

export function ViewApplicationModal({
  request,
  onClose,
  isPending = false,
  onApprove,
  onDecline,
  onMessage,
  onViewProfile,
  onViewCircle,
  onWithdraw,
  onEdit,
}: Props) {
  const t = useTranslations('Group');
  // Leaders open only rows that carry an intake snapshot; the requester's own
  // "View request" (onWithdraw set) opens for any pending row, form or not.
  const open =
    request !== null && (request.intake != null || onWithdraw !== undefined);
  const intake = request?.intake ?? null;

  const isCircle = request?.applicantType === 'circle';
  const circle = request?.subjectCircle ?? null;
  const acting = request?.actingUser ?? null;
  const subject = request?.subject ?? null;
  const group = request?.group ?? null;
  const applicantName = isCircle
    ? (circle?.name ?? t('admin.requests.unknownName'))
    : `${subject?.firstName ?? ''} ${subject?.lastName ?? ''}`.trim() ||
      t('admin.requests.unknownName');
  const actingName = `${acting?.firstName ?? ''} ${acting?.lastName ?? ''}`.trim();

  // Review target: a member applicant DMs / links to the requester; a Circle
  // applicant DMs / links to the Circle's acting leader (same rule as the card).
  const targetUserId = isCircle
    ? (acting?.userId ?? null)
    : (subject?.userId ?? null);
  const targetName = isCircle ? actingName : applicantName;

  const note = request?.message?.trim() ?? '';
  const submittedBy = isCircle && actingName.length > 0 ? actingName : null;
  const requestedAt =
    request !== null ? formatRelative(request.createdAt, t) : '';

  // The review action bar belongs only to a pending request, and only when the
  // caller wired the handlers (the applicant list on the Awaiting-review tab).
  const showActions =
    request?.status === 'pending' &&
    (onApprove !== undefined ||
      onDecline !== undefined ||
      onMessage !== undefined);

  // Entity-specific first footer action, chosen from the authoritative
  // applicant type (never from display text): a Circle applicant links to its
  // Circle detail page, a member applicant to their profile. Both occupy the
  // same footer slot with the same ghost icon-and-text treatment.
  const detailAction = isCircle
    ? onViewCircle !== undefined && circle?.id != null
      ? (
          <Button
            variant="ghost"
            size="md"
            icon={<Users size={16} strokeWidth={1.75} aria-hidden />}
            onClick={() => onViewCircle(circle.id)}
          >
            {t('admin.viewApplication.viewCircle')}
          </Button>
        )
      : null
    : onViewProfile !== undefined && targetUserId !== null
      ? (
          <Button
            variant="ghost"
            size="md"
            icon={<User size={16} strokeWidth={1.75} aria-hidden />}
            onClick={() => onViewProfile(targetUserId)}
          >
            {t('admin.viewApplication.viewProfile')}
          </Button>
        )
      : null;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      size="lg"
      accessibleTitle={t('admin.viewApplication.accessibleTitle', {
        name: applicantName,
      })}
    >
      <DialogSection bleed>
        <Row justify="space-between" align="flex-start" gap="lg" py="lg" px="lg">
          <Row gap="md" align="center" style={{ minWidth: 0 }}>
            <Avatar
              {...(isCircle
                ? { name: applicantName }
                : subject?.photoUrl
                  ? { src: subject.photoUrl, name: applicantName }
                  : { name: applicantName })}
              size={40}
            />
            <Stack gap="xs" style={{ minWidth: 0 }}>
              <Row gap="sm" align="center" wrap>
                <Heading level={3}>{applicantName}</Heading>
                {isCircle ? (
                  <Pill tone="navy" icon={I.circles}>
                    {t('admin.requests.circleApplicant')}
                  </Pill>
                ) : null}
              </Row>
              <Row gap="xs" align="center" wrap>
                {/* The destination group. A leader reviewing incoming rows IS
                    the destination, so it's noise there — but the requester's
                    own view (onWithdraw set) needs it, or two applications to
                    different Networks open indistinguishable modals. */}
                {(!isCircle || onWithdraw !== undefined) && group?.name ? (
                  <Pill tone="burgundy" icon={I.link}>
                    {group.name}
                  </Pill>
                ) : null}
                {submittedBy !== null ? (
                  <Body size="sm" color="mute">
                    {t('admin.viewApplication.submittedBy', {
                      name: submittedBy,
                    })}
                  </Body>
                ) : null}
                {requestedAt.length > 0 ? (
                  <Body size="sm" color="mute">
                    {submittedBy !== null || (!isCircle && group?.name)
                      ? '· '
                      : ''}
                    {t('admin.requests.requested', { time: requestedAt })}
                  </Body>
                ) : null}
              </Row>
            </Stack>
          </Row>
          <Button
            variant="ghost"
            size="sm"
            icon={I.x}
            aria-label={t('admin.viewApplication.close')}
            onClick={onClose}
          />
        </Row>
      </DialogSection>

      <DialogSection scrollable divider>
        {intake === null ? (
          // No intake form applied to this request — but the plain join flow
          // offers an optional note to the leader, submitted as `message`.
          // When one was written it IS the application; show it, not the
          // "no questions" placeholder.
          note.length > 0 ? (
            <Stack gap="xs" py="md">
              <Eyebrow>{t('admin.viewApplication.applicantNote')}</Eyebrow>
              <Body style={{ whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>
                {note}
              </Body>
            </Stack>
          ) : (
            <Stack gap="sm" align="center" py="xl">
              <Body size="sm" color="mute" style={{ textAlign: 'center' }}>
                {t('admin.viewApplication.noForm')}
              </Body>
            </Stack>
          )
        ) : isTombstone(intake) ? (
          <Stack gap="sm" align="center" py="xl">
            <Body size="sm" color="mute" style={{ textAlign: 'center' }}>
              {t('admin.viewApplication.scrubbed')}
            </Body>
          </Stack>
        ) : (
          <ApplicationBody intake={intake} moveShown={isCircle} />
        )}
      </DialogSection>

      {showActions ? (
        <DialogFooter>
          <Row
            justify="space-between"
            align="center"
            wrap
            gap="sm"
            style={{ flex: 1, minWidth: 0 }}
          >
            <Row gap="sm" align="center" wrap>
              {detailAction}
              {onMessage !== undefined && targetUserId !== null ? (
                <Button
                  variant="secondary"
                  size="md"
                  icon={I.chat}
                  onClick={() => onMessage(targetUserId)}
                  disabled={isPending}
                >
                  {t('admin.viewApplication.messageApplicant')}
                </Button>
              ) : null}
            </Row>
            <Row gap="sm" align="center">
              {onDecline !== undefined ? (
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => request !== null && onDecline(request.id)}
                  disabled={isPending}
                >
                  {t('admin.requests.decline')}
                </Button>
              ) : null}
              {onApprove !== undefined ? (
                <Button
                  variant="primary"
                  size="md"
                  icon={I.check}
                  onClick={() => request !== null && onApprove(request.id)}
                  disabled={isPending}
                >
                  {t('admin.requests.approve')}
                </Button>
              ) : null}
            </Row>
          </Row>
        </DialogFooter>
      ) : (
        <DialogFooter>
          <Row
            justify={onWithdraw !== undefined ? 'space-between' : 'flex-end'}
            align="center"
            gap="sm"
            wrap
            style={{ flex: 1, minWidth: 0 }}
          >
            {onWithdraw !== undefined ? (
              <Button
                variant="softDanger"
                size="md"
                onClick={onWithdraw}
                disabled={isPending}
              >
                {t('join.sent.withdraw')}
              </Button>
            ) : null}
            <Row gap="sm" align="center" wrap>
              {onEdit !== undefined ? (
                <Button
                  variant="secondary"
                  size="md"
                  icon={I.edit}
                  onClick={onEdit}
                  disabled={isPending}
                >
                  {t('join.sent.edit')}
                </Button>
              ) : null}
              <Button variant="secondary" onClick={onClose}>
                {t('admin.viewApplication.close')}
              </Button>
            </Row>
          </Row>
        </DialogFooter>
      )}
    </Dialog>
  );
}

function ApplicationBody({
  intake,
  moveShown,
}: {
  readonly intake: Snapshot;
  readonly moveShown: boolean;
}) {
  const t = useTranslations('Group');
  const answers = intake.answers ?? [];
  return (
    <Stack gap="lg">
      {answers.length === 0 ? (
        <Body size="sm" color="mute">
          {t('admin.viewApplication.noQuestions')}
        </Body>
      ) : (
        <Stack gap="none">
          {answers.map((a, i) => (
            <Fragment key={`${a.questionId}-${i}`}>
              {i > 0 ? <Divider dotted /> : null}
              <Stack gap="xs" py="md">
                <Eyebrow>{a.questionText}</Eyebrow>
                <AnswerValue answer={a} />
              </Stack>
            </Fragment>
          ))}
        </Stack>
      )}

      {moveShown && intake.moveConfirmed === true ? (
        <Pill tone="neutral" icon={I.check}>
          {t('admin.viewApplication.moveConfirmed')}
        </Pill>
      ) : null}

      <Divider dotted />
      <Stack gap="xs">
        <Eyebrow>{t('admin.viewApplication.consentHeading')}</Eyebrow>
        {intake.consent.statements.length > 0 ? (
          <Stack gap="sm" py="xs">
            {intake.consent.statements.map((s) => (
              <Row key={s.key} gap="sm" align="flex-start">
                <Icon size={16} color="var(--success)">
                  {I.check}
                </Icon>
                <Body size="md">{consentStatementLabel(t, s.key)}</Body>
              </Row>
            ))}
          </Stack>
        ) : (
          <Body size="sm" color="mute">
            {t('admin.viewApplication.consentGranted')}
          </Body>
        )}
      </Stack>
    </Stack>
  );
}

// The consent statements the applicant agreed to, keyed exactly as the applicant
// form renders them (mirrors IntakeFormFields' local map) so the reviewer sees
// the SAME statements they were shown — not a paraphrased summary.
function consentStatementLabel(
  t: ReturnType<typeof useTranslations<'Group'>>,
  key: string,
): string {
  switch (key) {
    case 'share_with_leaders':
      return t('intakeForm.consent.statements.share_with_leaders');
    case 'withdraw_right':
      return t('intakeForm.consent.statements.withdraw_right');
    default:
      return key;
  }
}

function AnswerValue({ answer }: { readonly answer: Snapshot['answers'][number] }) {
  const t = useTranslations('Group');
  if (answer.type === 'declaration') {
    if (answer.value === true) {
      return (
        <Row gap="xs" align="center">
          <Icon size={16} color="var(--success)">
            {I.check}
          </Icon>
          <Body size="md">{t('admin.viewApplication.declarationYes')}</Body>
        </Row>
      );
    }
    return <Body size="md">{t('admin.viewApplication.declarationNo')}</Body>;
  }
  const s = typeof answer.value === 'string' ? answer.value.trim() : '';
  return (
    <Body size="md" style={{ whiteSpace: 'pre-wrap' }}>
      {s.length > 0 ? s : t('admin.viewApplication.noAnswer')}
    </Body>
  );
}
